import { selectInProgressUnenrollment } from '@/reducers/inProgressUtilityProgramUnenrollments'
import { useUnenrollmentRouteContext } from '..'
import { useAppSelector } from '@/hooks'
import {
  CardActions,
  CardAction,
  Button,
  Text,
  LoadingAnimation,
  Icon,
} from '@/components'
import useViewModel from './UnenrollSuggestions.viewmodel'
import VehicleSuggestionCard from './components/VehicleSuggestionCard/VehicleSuggestionCard'
import classes from '@/utils/classes/classes'
import { UtilityProgram } from '@/types/utilityProgram'
import { match } from 'ts-pattern'
import ChargerSuggestionCard from './components/ChargerSuggestionCard/ChargerSuggestionCard'
import ThermostatSuggestionCard from './components/ThermostatSuggestionCard/ThermostatSuggestionCard'
import EnergySystemSuggestionCard from './components/EnergySystemSuggestionCard/EnergySystemSuggestionCard'
import ProgramSuggestionCard from './components/ProgramSuggestionCard/ProgramSuggestionCard'
import { MissingUnenrollmentReasonsError } from './UnenrollSuggestions.errors'

export type Props = Parameters<typeof useViewModel>[0] & {
  utilityProgram: UtilityProgram
}

function UnenrollSuggestionsContent(props: Props) {
  const { utilityProgram } = props
  const {
    makeLoadedSignalSetter,
    isLoadingOrIdle,
    onUnenrollClick,
    onStayInProgramClick,
    deviceSelectionForPresentation,
  } = useViewModel(props)

  return (
    <>
      {isLoadingOrIdle && <LoadingAnimation type="falling" centered />}
      <div
        className={classes('flex flex-col', {
          'h-full': !isLoadingOrIdle,
          'h-0 hidden': isLoadingOrIdle,
        })}
      >
        <div className="flex flex-col gap-4 mb-8 items-center">
          <div className="flex flex-col items-center justify-center w-12 h-12 bg-themed-purple-500 rounded-full">
            <Icon name="AlertCircle" color="white" size={20} />
          </div>
          <Text variant="h3" className="text-center">
            Looks like we can help before you go
          </Text>
          <Text variant="body2" className="text-center">
            Based on your feedback, you can adjust your settings or explore help
            articles to better fit your needs.
          </Text>
        </div>

        <div id="setting-suggestion-cards" className="flex flex-col gap-4">
          {deviceSelectionForPresentation.map((selection) => {
            return match(selection.deviceType)
              .with('vehicle', () => {
                return selection.ids.map((id) => {
                  const key = `vehicle-${id}`
                  return (
                    <VehicleSuggestionCard
                      key={key}
                      vehicleId={id as number}
                      setLoadedSignal={makeLoadedSignalSetter(key)}
                      articles={selection.articles}
                      settingsWhitelist={selection.settingsWhitelist}
                    />
                  )
                })
              })
              .with('charger', () => {
                return selection.ids.map((id) => {
                  const key = `charger-${id}`
                  return (
                    <ChargerSuggestionCard
                      key={key}
                      chargerId={id as number}
                      setLoadedSignal={makeLoadedSignalSetter(key)}
                      articles={selection.articles}
                      settingsWhitelist={selection.settingsWhitelist}
                    />
                  )
                })
              })
              .with('thermostat', () => {
                return selection.ids.map((id) => {
                  const key = `thermostat-${id}`
                  return (
                    <ThermostatSuggestionCard
                      key={key}
                      thermostatId={id as number}
                      setLoadedSignal={makeLoadedSignalSetter(key)}
                      articles={selection.articles}
                      settingsWhitelist={selection.settingsWhitelist}
                    />
                  )
                })
              })
              .with('energySystem', () => {
                return selection.ids.map((id) => {
                  const key = `energy-system-${id}`
                  return (
                    <EnergySystemSuggestionCard
                      key={key}
                      energySystemId={id as string}
                      setLoadedSignal={makeLoadedSignalSetter(key)}
                      articles={selection.articles}
                    />
                  )
                })
              })
              .with('profile', () => {
                return (
                  <ProgramSuggestionCard
                    key={`program-${utilityProgram.id}`}
                    programId={utilityProgram.id}
                    setLoadedSignal={makeLoadedSignalSetter('program')}
                    articles={selection.articles}
                  />
                )
              })
              .exhaustive()
          })}
        </div>

        <span className="flex-auto"></span>
        <CardActions className="mt-8">
          <CardAction type="secondary">
            <Button
              variant="primary-subtle"
              id="unenroll-suggestions-unenroll"
              onClick={onUnenrollClick}
            >
              Continue to Unenroll
            </Button>
          </CardAction>
          <CardAction type="primary">
            <Button
              variant="primary"
              id="unenroll-suggestions-stay-in-program"
              onClick={onStayInProgramClick}
            >
              Stay in Program
            </Button>
          </CardAction>
        </CardActions>
      </div>
    </>
  )
}

export default function UnenrollSuggestions() {
  const { utilityProgram } = useUnenrollmentRouteContext()
  const inProgressUnenrollment = useAppSelector(
    selectInProgressUnenrollment(utilityProgram.id),
  )

  if (!inProgressUnenrollment) {
    return null
  }

  if (!inProgressUnenrollment.unenrollmentReasons) {
    throw new MissingUnenrollmentReasonsError(
      `No unenrollment reasons found for utility program ${utilityProgram.id}`,
    )
  }

  return (
    <UnenrollSuggestionsContent
      utilityProgram={utilityProgram}
      deviceSelection={inProgressUnenrollment.deviceSelection}
      selectedReasons={inProgressUnenrollment.unenrollmentReasons}
    />
  )
}
